import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Heart, 
  Users,
  Plus,
  Send,
  Loader2,
  Lock,
  Crown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrayerWall() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newPrayer, setNewPrayer] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isPremium = user?.is_premium;

  useEffect(() => {
    fetchPrayers();
  }, []);

  const fetchPrayers = async () => {
    try {
      const res = await axios.get(`${API}/prayers`);
      setPrayers(res.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load prayers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitPrayer = async () => {
    if (!newPrayer.trim()) {
      toast.error('Please enter your prayer request');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/prayers`, {
        content: newPrayer,
        is_anonymous: isAnonymous
      });
      setPrayers([res.data, ...prayers]);
      setDialogOpen(false);
      setNewPrayer('');
      setIsAnonymous(false);
      toast.success('Prayer request shared with the sisterhood');
    } catch (error) {
      toast.error('Failed to submit prayer');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrayFor = async (prayerId) => {
    try {
      const res = await axios.post(`${API}/prayers/${prayerId}/pray`);
      setPrayers(prayers.map(p => 
        p.id === prayerId ? { ...p, prayer_count: res.data.prayer_count } : p
      ));
      toast('Praying with you, sister', { icon: '🙏' });
    } catch (error) {
      toast.error('Failed to record prayer');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-10 max-w-4xl mx-auto" data-testid="prayer-wall-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
          <div>
            <h1 className="font-serif text-3xl lg:text-4xl font-bold text-foreground mb-2">
              Sisterhood Prayer Wall
            </h1>
            <p className="text-muted-foreground">
              We lift each other up. Share your heart. We're praying.
            </p>
          </div>
          {isPremium ? (
            <Button
              onClick={() => setDialogOpen(true)}
              data-testid="add-prayer-btn"
              className="rounded-full px-6 bg-primary hover:bg-primary/90 whitespace-nowrap"
            >
              <Plus className="w-4 h-4 mr-2" />
              Share Request
            </Button>
          ) : (
            <Button
              onClick={() => navigate('/subscription')}
              variant="outline"
              className="rounded-full px-6 border-dashed whitespace-nowrap"
            >
              <Lock className="w-4 h-4 mr-2" />
              Post (Premium)
            </Button>
          )}
        </div>

        {/* Community Image */}
        <div className="relative rounded-2xl overflow-hidden mb-10 h-48">
          <img 
            src="https://images.unsplash.com/photo-1673137757277-48c481c29e24?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHx3b21lbiUyMGhvbGRpbmclMjBoYW5kcyUyMGNpcmNsZSUyMHN1bnNldHxlbnwwfHx8fDE3NzEwODk5MzB8MA&ixlib=rb-4.1.0&q=85"
            alt="Sisterhood"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-purple-900/80 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6" />
              <span className="font-semibold">You are not alone in this journey</span>
            </div>
          </div>
        </div>

        {/* Prayer Requests */}
        {prayers.length === 0 ? (
          <div className="text-center py-16 crown-card">
            <Heart className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">No prayer requests yet</p>
            <Button
              onClick={() => setDialogOpen(true)}
              variant="outline"
              className="rounded-full"
            >
              Be the first to share
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {prayers.map((prayer, idx) => (
              <div 
                key={prayer.id}
                className="crown-card p-6 animate-fade-in"
                style={{ animationDelay: `${idx * 0.05}s` }}
                data-testid={`prayer-${prayer.id}`}
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white font-semibold">
                      {prayer.author_name[0]}
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{prayer.author_name}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(prayer.created_at)}</p>
                    </div>
                  </div>
                </div>
                
                <p className="text-foreground mb-4 leading-relaxed">{prayer.content}</p>
                
                <div className="flex items-center justify-between">
                  <Button
                    onClick={() => handlePrayFor(prayer.id)}
                    variant="ghost"
                    size="sm"
                    data-testid={`pray-btn-${prayer.id}`}
                    className="text-muted-foreground hover:text-primary hover:bg-primary/5 rounded-full"
                  >
                    <Heart className="w-4 h-4 mr-2" />
                    Pray with her
                  </Button>
                  {prayer.prayer_count > 0 && (
                    <span className="text-sm text-muted-foreground">
                      {prayer.prayer_count} {prayer.prayer_count === 1 ? 'sister' : 'sisters'} praying
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Prayer Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent data-testid="prayer-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl flex items-center gap-2">
                <Heart className="w-6 h-6 text-primary" />
                Share Your Prayer Request
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <Textarea
                data-testid="prayer-input"
                placeholder="Share what's on your heart. Your sisters are here for you..."
                value={newPrayer}
                onChange={(e) => setNewPrayer(e.target.value)}
                className="min-h-[150px] resize-none"
              />
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="anonymous"
                  data-testid="anonymous-checkbox"
                  checked={isAnonymous}
                  onCheckedChange={setIsAnonymous}
                />
                <Label htmlFor="anonymous" className="text-sm text-muted-foreground cursor-pointer">
                  Post anonymously
                </Label>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => setDialogOpen(false)}
                  variant="outline"
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitPrayer}
                  disabled={submitting || !newPrayer.trim()}
                  data-testid="submit-prayer-btn"
                  className="flex-1 rounded-full bg-primary hover:bg-primary/90"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Share Request
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
